'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type * as React from 'react';

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------

export interface Terminal404Prompt {
  user?: string;
  host?: string;
}

export interface Terminal404Props {
  /** Path that "doesn't exist" — shown as the boot `cd …` failure. Defaults to window.location.pathname. */
  pathname?: string;
  /** Prompt user/host. Defaults to root@drive-manager. */
  prompt?: Terminal404Prompt;
  /** Called when the user types `home` / `exit` or clicks the home button. Defaults to `window.location.assign('/')`. */
  onNavigateHome?: () => void;
  /** Show the top-right home button. Default true. */
  homeButton?: boolean;
  /** Override the boot banner shown above the failed `cd`. */
  bootMessage?: string;
  /** Enable the kernel-panic destruct sequence on `rm -rf /`. Default true. */
  heartAttack?: boolean;
  /** Optional className applied to the outer wrapper. */
  className?: string;
}

// ----------------------------------------------------------------------------
// Virtual filesystem
// ----------------------------------------------------------------------------

type FsNode =
  | { type: 'dir'; children: Record<string, FsNode> }
  | { type: 'file'; content: string };

const HOME = '/root';

function makeInitialFs(): FsNode {
  return {
    type: 'dir',
    children: {
      root: {
        type: 'dir',
        children: {
          'readme.txt': {
            type: 'file',
            content:
              "you took a wrong turn somewhere. that's ok — you're root, you can do whatever you want.\n" +
              '\n' +
              'try `help` to see what works here, or `home` to go back.\n' +
              'this terminal is fake — none of it touches the server.\n',
          },
          'notes.md': {
            type: 'file',
            content:
              '# things to try\n' +
              '\n' +
              '- ls / pwd / cd ..\n' +
              '- mkdir something && cd something\n' +
              '- cat readme.txt\n' +
              '- apt install coffee\n' +
              '- rm -rf /  (yes, really)\n' +
              '- vim, nano, emacs (pick a side)\n' +
              '- home  → exit and go back to the real app\n',
          },
          projects: { type: 'dir', children: {} },
        },
      },
      home: { type: 'dir', children: {} },
      etc: {
        type: 'dir',
        children: {
          hostname: { type: 'file', content: 'localhost\n' },
          motd: { type: 'file', content: "you're off the map. nothing here.\n" },
        },
      },
      tmp: { type: 'dir', children: {} },
      var: {
        type: 'dir',
        children: {
          log: {
            type: 'dir',
            children: {
              '404.log': {
                type: 'file',
                content:
                  '[ERR] route not found\n' +
                  '[INFO] showing terminal fallback\n' +
                  '[INFO] user is probably confused (and that is fine)\n',
              },
            },
          },
        },
      },
    },
  };
}

function splitPath(p: string): string[] {
  return p.split('/').filter(Boolean);
}

function joinPath(parts: string[]): string {
  return '/' + parts.join('/');
}

function resolvePath(cwd: string, target: string): string {
  if (!target) return cwd;
  if (target === '~') return HOME;
  if (target.startsWith('~/')) return resolvePath('/', HOME.slice(1) + '/' + target.slice(2));
  const start = target.startsWith('/') ? [] : splitPath(cwd);
  const parts = [...start];
  for (const seg of splitPath(target)) {
    if (seg === '.' || seg === '') continue;
    if (seg === '..') parts.pop();
    else parts.push(seg);
  }
  return joinPath(parts);
}

function getNode(root: FsNode, path: string): FsNode | null {
  const parts = splitPath(path);
  let cur: FsNode = root;
  for (const seg of parts) {
    if (cur.type !== 'dir' || !(seg in cur.children)) return null;
    cur = cur.children[seg];
  }
  return cur;
}

function getParent(root: FsNode, path: string): { parent: FsNode; name: string } | null {
  const parts = splitPath(path);
  if (parts.length === 0) return null;
  const name = parts[parts.length - 1];
  const parentPath = joinPath(parts.slice(0, -1));
  const parent = getNode(root, parentPath);
  if (!parent || parent.type !== 'dir') return null;
  return { parent, name };
}

function cloneFs(node: FsNode): FsNode {
  if (node.type === 'file') return { type: 'file', content: node.content };
  const children: Record<string, FsNode> = {};
  for (const [k, v] of Object.entries(node.children)) children[k] = cloneFs(v);
  return { type: 'dir', children };
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

interface Line {
  kind: 'in' | 'out' | 'err' | 'sys';
  text: string;
}

function shortPath(path: string): string {
  if (path === HOME) return '~';
  if (path.startsWith(HOME + '/')) return '~' + path.slice(HOME.length);
  return path;
}

const HELP_TEXT = [
  'available commands:',
  '  ls [path]            list directory',
  '  cd <path>            change directory  (cd, cd .., cd ~, cd /)',
  '  pwd                  print working directory',
  '  mkdir <name>         create a directory',
  '  touch <name>         create an empty file',
  '  cat <file>           print a file',
  '  echo <text>          echo text   (>> file appends, > file overwrites)',
  '  rm [-rf] <name>      remove a file (or directory with -rf)',
  '  clear                clear the screen',
  '  whoami / date        the basics',
  '  history              recent commands',
  '  apt install <pkg>    install something. or pretend to.',
  '  npm install <pkg>    same energy.',
  '  sudo <cmd>           with great power...',
  '  vim / nano / emacs   choose your fighter',
  '  fortune              wisdom',
  '  cowsay <text>        moo',
  '  home / exit          back to the real app',
].join('\n');

export function Terminal404({
  pathname,
  prompt,
  onNavigateHome,
  homeButton = true,
  bootMessage,
  heartAttack = true,
  className,
}: Terminal404Props = {}) {
  const promptUser = prompt?.user ?? 'root';
  const promptHost = prompt?.host ?? 'localhost';
  const banner = bootMessage ?? 'terminal · 404 fallback · v1.0.0';

  // Resolve the failing path lazily so SSR doesn't blow up on `window`.
  const failedPath = useMemo(() => {
    if (pathname) return pathname;
    if (typeof window !== 'undefined') return window.location.pathname;
    return '/unknown';
  }, [pathname]);

  const goHome = useCallback(() => {
    if (onNavigateHome) onNavigateHome();
    else if (typeof window !== 'undefined') window.location.assign('/');
  }, [onNavigateHome]);

  const [fs, setFs] = useState<FsNode>(() => makeInitialFs());
  const [cwd, setCwd] = useState<string>(HOME);
  const [history, setHistory] = useState<Line[]>(() => [
    { kind: 'sys', text: banner },
    { kind: 'in', text: `${promptUser}@${promptHost}:~# cd ${failedPath}` },
    { kind: 'err', text: `cd: no such file or directory: ${failedPath}` },
    { kind: 'sys', text: 'type `help` to see what works here, or `home` to bail.' },
    { kind: 'sys', text: '' },
  ]);
  const [input, setInput] = useState('');
  const [cmdHistory, setCmdHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState<number | null>(null);
  const [destructPhase, setDestructPhase] = useState<'idle' | 'destroying' | 'flash' | 'dead'>('idle');

  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const append = useCallback((lines: Line[]) => {
    setHistory((h) => [...h, ...lines]);
  }, []);

  const promptLine = useCallback(
    (cmd: string): Line => ({
      kind: 'in',
      text: `${promptUser}@${promptHost}:${shortPath(cwd)}# ${cmd}`,
    }),
    [cwd, promptUser, promptHost]
  );

  const runDestruct = useCallback(async () => {
    if (!heartAttack) {
      append([{ kind: 'err', text: "rm: refusing to operate on '/'. (heart attack disabled.)" }]);
      return;
    }
    setDestructPhase('destroying');

    const rapid: Line[] = [
      { kind: 'sys', text: 'rm: --no-preserve-root accepted.' },
      { kind: 'sys', text: '' },
      { kind: 'err', text: 'unlink: /etc' },
      { kind: 'err', text: 'unlink: /home' },
      { kind: 'err', text: 'unlink: /var' },
      { kind: 'err', text: 'unlink: /usr' },
      { kind: 'err', text: 'unlink: /boot' },
      { kind: 'err', text: 'unlink: /lib' },
      { kind: 'err', text: 'unlink: /bin' },
      { kind: 'err', text: 'unlink: /dev/sda1' },
      { kind: 'sys', text: '' },
      { kind: 'err', text: '[FATAL] kernel panic - not syncing: VFS: Unable to mount root fs on unknown-block(0,0)' },
      { kind: 'err', text: '[FATAL] CPU: 0 PID: 1 Comm: init Tainted: G    D' },
      { kind: 'err', text: '[FATAL] Hardware: react-terminal-404 v1.0.0 / chip rev 0xdeadbeef' },
      { kind: 'err', text: '[FATAL] Call Trace:' },
      { kind: 'err', text: '[FATAL]   ? do_exit+0x500/0x600' },
      { kind: 'err', text: '[FATAL]   ? sys_unlink+0x80/0x100' },
      { kind: 'err', text: '[FATAL]   ? page_fault+0x1f/0x30' },
      { kind: 'err', text: '[FATAL]   ? __schedule+0x290/0x9d0' },
      { kind: 'err', text: '[FATAL] Code: 48 89 e5 41 57 41 56 41 55 41 54 53 50 48 8b 05' },
      { kind: 'err', text: '[FATAL] RIP: 0010:do_exit+0x500/0x600' },
      { kind: 'err', text: '[FATAL] CR2: 00000000deadbeef CR3: 00000000cafef00d' },
      { kind: 'err', text: 'Segmentation fault (core dumped)' },
    ];
    for (const l of rapid) {
      append([l]);
      await sleep(45);
    }

    setDestructPhase('flash');
    await sleep(450);
    setDestructPhase('dead');
    await sleep(2400);

    setHistory([
      { kind: 'sys', text: banner },
      { kind: 'sys', text: '' },
    ]);
    setFs(makeInitialFs());
    setCwd(HOME);
    setDestructPhase('idle');
  }, [append, banner, heartAttack]);

  const run = useCallback(
    async (raw: string) => {
      const trimmed = raw.trim();
      append([promptLine(raw)]);
      if (!trimmed) return;
      setCmdHistory((h) => [...h, trimmed]);

      const tokens = tokenize(trimmed);
      const [cmd, ...args] = tokens;

      const out: Line[] = [];
      const err: Line[] = [];
      let next = cwd;
      let nextFs = fs;

      switch (cmd) {
        case 'help':
        case '?':
          out.push({ kind: 'out', text: HELP_TEXT });
          break;
        case 'pwd':
          out.push({ kind: 'out', text: cwd });
          break;
        case 'whoami':
          out.push({ kind: 'out', text: promptUser });
          break;
        case 'date':
          out.push({ kind: 'out', text: new Date().toString() });
          break;
        case 'clear':
        case 'cls':
          setHistory([]);
          return;
        case 'history': {
          const lines = cmdHistory.map((c, i) => `  ${String(i + 1).padStart(3)}  ${c}`);
          out.push({ kind: 'out', text: lines.join('\n') || '(empty)' });
          break;
        }
        case 'ls': {
          const target = args[0] ? resolvePath(cwd, args[0]) : cwd;
          const node = getNode(fs, target);
          if (!node) {
            err.push({ kind: 'err', text: `ls: cannot access '${args[0] ?? target}': No such file or directory` });
            break;
          }
          if (node.type === 'file') {
            out.push({ kind: 'out', text: shortPath(target) });
            break;
          }
          const names = Object.keys(node.children).sort();
          if (names.length === 0) {
            out.push({ kind: 'out', text: '(empty)' });
            break;
          }
          const colored = names.map((n) => {
            const child = node.children[n];
            return child.type === 'dir' ? `\x1bDIR\x1b${n}/` : n;
          });
          out.push({ kind: 'out', text: colored.join('  ') });
          break;
        }
        case 'cd': {
          const target = args[0] ? resolvePath(cwd, args[0]) : HOME;
          const node = getNode(fs, target);
          if (!node) {
            err.push({ kind: 'err', text: `cd: no such file or directory: ${args[0] ?? '~'}` });
            break;
          }
          if (node.type !== 'dir') {
            err.push({ kind: 'err', text: `cd: not a directory: ${args[0]}` });
            break;
          }
          next = target;
          break;
        }
        case 'mkdir': {
          if (!args[0]) {
            err.push({ kind: 'err', text: 'mkdir: missing operand' });
            break;
          }
          const path = resolvePath(cwd, args[0]);
          const p = getParent(fs, path);
          if (!p) {
            err.push({ kind: 'err', text: `mkdir: cannot create '${args[0]}'` });
            break;
          }
          if (p.parent.type !== 'dir' || p.name in p.parent.children) {
            err.push({ kind: 'err', text: `mkdir: cannot create '${args[0]}': File exists` });
            break;
          }
          const newFs = cloneFs(fs);
          const np = getParent(newFs, path)!;
          (np.parent as { type: 'dir'; children: Record<string, FsNode> }).children[np.name] = { type: 'dir', children: {} };
          nextFs = newFs;
          break;
        }
        case 'touch': {
          if (!args[0]) {
            err.push({ kind: 'err', text: 'touch: missing operand' });
            break;
          }
          const path = resolvePath(cwd, args[0]);
          const p = getParent(fs, path);
          if (!p || p.parent.type !== 'dir') {
            err.push({ kind: 'err', text: `touch: cannot touch '${args[0]}': No such file or directory` });
            break;
          }
          if (!(p.name in p.parent.children)) {
            const newFs = cloneFs(fs);
            const np = getParent(newFs, path)!;
            (np.parent as { type: 'dir'; children: Record<string, FsNode> }).children[np.name] = { type: 'file', content: '' };
            nextFs = newFs;
          }
          break;
        }
        case 'cat': {
          if (!args[0]) {
            err.push({ kind: 'err', text: 'cat: missing operand' });
            break;
          }
          const path = resolvePath(cwd, args[0]);
          const node = getNode(fs, path);
          if (!node) {
            err.push({ kind: 'err', text: `cat: ${args[0]}: No such file or directory` });
            break;
          }
          if (node.type !== 'file') {
            err.push({ kind: 'err', text: `cat: ${args[0]}: Is a directory` });
            break;
          }
          out.push({ kind: 'out', text: node.content.replace(/\n$/, '') });
          break;
        }
        case 'echo': {
          const redirIdx = args.findIndex((a) => a === '>' || a === '>>');
          if (redirIdx >= 0) {
            const text = args.slice(0, redirIdx).join(' ');
            const mode = args[redirIdx];
            const target = args[redirIdx + 1];
            if (!target) {
              err.push({ kind: 'err', text: 'echo: missing redirect target' });
              break;
            }
            const path = resolvePath(cwd, target);
            const newFs = cloneFs(fs);
            const p = getParent(newFs, path);
            if (!p || p.parent.type !== 'dir') {
              err.push({ kind: 'err', text: `echo: cannot write '${target}'` });
              break;
            }
            const existing = p.parent.children[p.name];
            const prev = existing && existing.type === 'file' ? existing.content : '';
            const content = mode === '>>' ? prev + text + '\n' : text + '\n';
            p.parent.children[p.name] = { type: 'file', content };
            nextFs = newFs;
          } else {
            out.push({ kind: 'out', text: args.join(' ') });
          }
          break;
        }
        case 'rm': {
          const flags = args.filter((a) => a.startsWith('-'));
          const rest = args.filter((a) => !a.startsWith('-'));
          const recursive = flags.some((f) => f.includes('r')) || flags.some((f) => f.includes('R'));
          const force = flags.some((f) => f.includes('f'));
          if (rest.length === 0) {
            err.push({ kind: 'err', text: 'rm: missing operand' });
            break;
          }
          const target = rest[0];
          if (target === '/' || resolvePath(cwd, target) === '/') {
            if (!(recursive && force)) {
              err.push({ kind: 'err', text: "rm: it is dangerous to operate recursively on '/'" });
              err.push({ kind: 'err', text: 'rm: use --no-preserve-root to override this failsafe' });
              break;
            }
            await runDestruct();
            return;
          }
          const path = resolvePath(cwd, target);
          const p = getParent(fs, path);
          if (!p || p.parent.type !== 'dir' || !(p.name in p.parent.children)) {
            if (!force) err.push({ kind: 'err', text: `rm: cannot remove '${target}': No such file or directory` });
            break;
          }
          const node = p.parent.children[p.name];
          if (node.type === 'dir' && !recursive) {
            err.push({ kind: 'err', text: `rm: cannot remove '${target}': Is a directory` });
            break;
          }
          const newFs = cloneFs(fs);
          const np = getParent(newFs, path)!;
          delete (np.parent as { type: 'dir'; children: Record<string, FsNode> }).children[np.name];
          nextFs = newFs;
          break;
        }
        case 'apt':
        case 'apt-get': {
          if (args[0] !== 'install') {
            out.push({ kind: 'out', text: `usage: ${cmd} install <package>` });
            break;
          }
          const pkg = args.slice(1).join(' ') || 'nothing';
          await fakeInstall(pkg, cmd, append);
          return;
        }
        case 'npm':
        case 'pnpm':
        case 'yarn': {
          const sub = args[0] === 'install' || args[0] === 'add' || args[0] === 'i' ? args[0] : null;
          if (!sub) {
            out.push({ kind: 'out', text: `usage: ${cmd} install <package>` });
            break;
          }
          const pkg = args.slice(1).join(' ') || 'nothing';
          await fakeInstall(pkg, cmd, append);
          return;
        }
        case 'sudo': {
          const rest = args.join(' ');
          if (!rest) {
            err.push({ kind: 'err', text: 'usage: sudo <command>' });
            break;
          }
          if (rest === 'rm -rf /' || rest === 'rm -rf /*' || rest === 'rm -fr /') {
            await runDestruct();
            return;
          }
          out.push({ kind: 'out', text: 'you are already root. behave.' });
          break;
        }
        case 'vim':
        case 'vi':
          out.push({ kind: 'out', text: 'cannot exit vim. (just kidding — but no editor here.)' });
          break;
        case 'nano':
          out.push({ kind: 'out', text: 'GNU nano: respectfully declined.' });
          break;
        case 'emacs':
          out.push({ kind: 'out', text: 'emacs is an operating system. we already have one of those.' });
          break;
        case 'git':
          if (args[0] === 'commit') {
            out.push({ kind: 'out', text: '[detached HEAD root-commit deadbeef] WIP\n 0 files changed, your hopes raised' });
          } else if (args[0] === 'push') {
            out.push({ kind: 'out', text: "remote: Permission denied. you don't have a remote. you don't even have a repo." });
          } else if (args[0] === 'status') {
            out.push({ kind: 'out', text: 'On branch lost\nNothing to commit, you took a wrong turn' });
          } else {
            out.push({ kind: 'out', text: 'usage: git (commit|push|status) ...' });
          }
          break;
        case 'make':
          if (args[0] === 'coffee') {
            out.push({ kind: 'out', text: "418 I'm a teapot" });
          } else {
            out.push({ kind: 'out', text: `make: *** No rule to make target '${args[0] ?? ''}'.  Stop.` });
          }
          break;
        case 'fortune':
          out.push({ kind: 'out', text: pickFortune() });
          break;
        case 'cowsay':
          out.push({ kind: 'out', text: cowsay(args.join(' ') || 'moo') });
          break;
        case 'home':
        case 'exit':
        case 'quit':
        case 'logout': {
          out.push({ kind: 'sys', text: 'goodbye.' });
          setHistory((h) => [...h, ...out]);
          window.setTimeout(goHome, 250);
          return;
        }
        case '404':
          out.push({ kind: 'out', text: 'you are here.' });
          break;
        default:
          err.push({ kind: 'err', text: `${cmd}: command not found. try \`help\`.` });
      }

      if (out.length) append(out);
      if (err.length) append(err);
      if (next !== cwd) setCwd(next);
      if (nextFs !== fs) setFs(nextFs);
    },
    [append, cmdHistory, cwd, fs, goHome, promptLine, promptUser, runDestruct]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const v = input;
        setInput('');
        setHistoryIdx(null);
        void run(v);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (cmdHistory.length === 0) return;
        const idx = historyIdx === null ? cmdHistory.length - 1 : Math.max(0, historyIdx - 1);
        setHistoryIdx(idx);
        setInput(cmdHistory[idx]);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (historyIdx === null) return;
        const idx = historyIdx + 1;
        if (idx >= cmdHistory.length) {
          setHistoryIdx(null);
          setInput('');
        } else {
          setHistoryIdx(idx);
          setInput(cmdHistory[idx]);
        }
        return;
      }
      if (e.key === 'l' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setHistory([]);
        return;
      }
      if (e.key === 'c' && e.ctrlKey) {
        e.preventDefault();
        append([promptLine(input), { kind: 'sys', text: '^C' }]);
        setInput('');
      }
    },
    [append, cmdHistory, historyIdx, input, promptLine, run]
  );

  const promptText = useMemo(
    () => `${promptUser}@${promptHost}:${shortPath(cwd)}#`,
    [cwd, promptUser, promptHost]
  );

  const isDestroying = destructPhase === 'destroying';
  const isFlash = destructPhase === 'flash';
  const isDead = destructPhase === 'dead';

  return (
    <div onClick={focusInput} className={joinClass('t404-root', className)}>
      <style>{TERMINAL_STYLES}</style>
      <div className={joinClass('t404-window', isDestroying && 't404-destruct')}>
        <div className="t404-titlebar">
          <span className="t404-dot t404-dot-red" />
          <span className="t404-dot t404-dot-yellow" />
          <span className="t404-dot t404-dot-green" />
          <span className="t404-titlebar-label">{promptUser}@{promptHost} — bash — 80×24</span>
          {homeButton && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goHome();
              }}
              className="t404-home-btn"
              title="back to the real app"
            >
              home →
            </button>
          )}
        </div>
        <div ref={scrollRef} className="t404-console">
          {history.map((line, i) => (
            <pre key={i} className={joinClass('t404-line', `t404-line-${line.kind}`)}>
              {renderAnsi(line.text)}
            </pre>
          ))}
          {destructPhase === 'idle' && (
            <div className="t404-input-row">
              <span className="t404-prompt">{promptText}</span>
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                autoComplete="off"
                className="t404-input"
                aria-label="terminal input"
              />
            </div>
          )}
        </div>
      </div>

      {isFlash && <div className="t404-overlay t404-overlay-flash" aria-hidden />}
      {isDead && <div className="t404-overlay t404-overlay-dead" aria-hidden />}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function joinClass(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

function tokenize(s: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quote: '"' | "'" | null = null;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (quote) {
      if (ch === quote) quote = null;
      else cur += ch;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch as '"' | "'";
      continue;
    }
    if (ch === ' ' || ch === '\t') {
      if (cur) { out.push(cur); cur = ''; }
      continue;
    }
    cur += ch;
  }
  if (cur) out.push(cur);
  return out;
}

// `\x1bDIR\x1b` brackets dir names so we can color them in `ls` output.
function renderAnsi(text: string): React.ReactNode {
  if (!text.includes('\x1b')) return text;
  const parts = text.split(/\x1b/);
  const nodes: React.ReactNode[] = [];
  let dir = false;
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (p === 'DIR') {
      dir = !dir;
      continue;
    }
    nodes.push(
      dir ? <span key={i} className="t404-dir">{p}</span> : <span key={i}>{p}</span>
    );
  }
  return nodes;
}

async function fakeInstall(pkg: string, manager: string, append: (lines: Line[]) => void) {
  const steps = [
    'Reading package lists...',
    'Building dependency tree...',
    'The following NEW packages will be installed:',
    `  ${pkg}`,
    'Need to get 0 B of archives.',
    'After this operation, 0 B of additional disk space will be used.',
    `Get:1 https://example.invalid/ ${pkg} all 1.0.0 [????]`,
  ];
  for (const s of steps) {
    append([{ kind: 'out', text: s }]);
    await sleep(80);
  }
  for (let pct = 0; pct <= 100; pct += 20) {
    append([{ kind: 'out', text: progressBar(`Unpacking ${pkg}`, pct) }]);
    await sleep(120);
  }
  append([
    { kind: 'out', text: `Setting up ${pkg} (1.0.0) ...` },
    { kind: 'out', text: `${manager}: just kidding — no packages were harmed.` },
  ]);
}

function progressBar(label: string, pct: number): string {
  const width = 24;
  const filled = Math.round((pct / 100) * width);
  const bar = '█'.repeat(filled) + '·'.repeat(width - filled);
  return `${label}  [${bar}] ${pct}%`;
}

function sleep(ms: number) {
  return new Promise<void>((r) => window.setTimeout(r, ms));
}

const FORTUNES = [
  '"there are only two hard things in computer science: cache invalidation, naming things, and off-by-one errors."',
  '"it works on my machine." — every developer, before a 3am page',
  '"a user interface is like a joke. if you have to explain it, it\'s not that good."',
  '"the best error message is the one that never shows up." — thomas fuchs',
  '"weeks of coding can save you hours of planning." — anonymous',
];
function pickFortune(): string {
  return FORTUNES[Math.floor(Math.random() * FORTUNES.length)];
}

function cowsay(msg: string): string {
  const len = Math.max(4, msg.length);
  const top = ' ' + '_'.repeat(len + 2);
  const bot = ' ' + '-'.repeat(len + 2);
  return [
    top,
    `< ${msg} >`,
    bot,
    '        \\   ^__^',
    '         \\  (oo)\\_______',
    '            (__)\\       )\\/\\',
    '                ||----w |',
    '                ||     ||',
  ].join('\n');
}

// ----------------------------------------------------------------------------
// Styles (inlined so users don't have to import a CSS file)
// ----------------------------------------------------------------------------

const TERMINAL_STYLES = `
.t404-root {
  min-height: 100vh;
  width: 100%;
  background: #0b0d10;
  color: #6ee7b7;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 13px;
  line-height: 1.5;
  box-sizing: border-box;
}
.t404-root *, .t404-root *::before, .t404-root *::after { box-sizing: inherit; }
.t404-root ::selection { background: rgba(110, 231, 183, 0.3); }

.t404-window {
  margin: 0 auto;
  display: flex;
  height: 100vh;
  max-width: 64rem;
  flex-direction: column;
  padding: 1rem;
}
@media (min-width: 640px) {
  .t404-window { padding: 1.5rem; }
}

.t404-titlebar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(6, 78, 59, 0.6);
  background: rgba(2, 44, 34, 0.4);
  border-top-left-radius: 0.375rem;
  border-top-right-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
}
.t404-dot {
  display: inline-block;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 9999px;
}
.t404-dot-red    { background: rgba(239, 68, 68, 0.8); }
.t404-dot-yellow { background: rgba(234, 179, 8, 0.8); }
.t404-dot-green  { background: rgba(34, 197, 94, 0.8); }
.t404-titlebar-label {
  margin-left: 0.5rem;
  font-size: 0.75rem;
  color: rgba(167, 243, 208, 0.6);
}

.t404-home-btn {
  margin-left: auto;
  border: 1px solid rgba(6, 78, 59, 0.6);
  background: transparent;
  color: rgba(167, 243, 208, 0.7);
  font-family: inherit;
  font-size: 11px;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  cursor: pointer;
}
.t404-home-btn:hover { background: rgba(6, 78, 59, 0.4); }

.t404-console {
  flex: 1;
  overflow: auto;
  border: 1px solid rgba(6, 78, 59, 0.6);
  border-top: 0;
  border-bottom-left-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
  background: rgba(0, 0, 0, 0.4);
  padding: 1rem;
}

.t404-line {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  font-family: inherit;
}
.t404-line-in  { color: #a7f3d0; }
.t404-line-err { color: #f87171; }
.t404-line-sys { color: rgba(52, 211, 153, 0.7); }
.t404-line-out { color: #d1fae5; }
.t404-dir      { color: #67e8f9; }

.t404-input-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}
.t404-prompt {
  flex-shrink: 0;
  color: #34d399;
}
.t404-input {
  flex: 1;
  background: transparent;
  border: 0;
  outline: 0;
  color: #d1fae5;
  caret-color: #6ee7b7;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  padding: 0;
}

.t404-overlay {
  position: fixed;
  inset: 0;
  pointer-events: none;
}
.t404-overlay-flash {
  background: #dc2626;
  z-index: 100;
  animation: t404-flash 450ms ease-out forwards;
}
.t404-overlay-dead {
  background: #000;
  z-index: 101;
}

@keyframes t404-shake {
  0%, 100% { transform: translate3d(0, 0, 0); }
  10%      { transform: translate3d(-3px, 1px, 0) skewX(0.2deg); }
  20%      { transform: translate3d(3px, -2px, 0); }
  30%      { transform: translate3d(-2px, 3px, 0) skewY(-0.3deg); }
  40%      { transform: translate3d(3px, -1px, 0); }
  50%      { transform: translate3d(-3px, 2px, 0); }
  60%      { transform: translate3d(2px, -3px, 0) skewX(-0.2deg); }
  70%      { transform: translate3d(-2px, 1px, 0); }
  80%      { transform: translate3d(3px, 2px, 0); }
  90%      { transform: translate3d(-1px, -2px, 0); }
}
@keyframes t404-flicker {
  0%, 100% { opacity: 1; filter: none; }
  8%       { opacity: 0.35; }
  12%      { opacity: 1; }
  20%      { opacity: 0.7; filter: hue-rotate(15deg); }
  24%      { opacity: 1; filter: none; }
  52%      { opacity: 0.85; filter: brightness(1.4); }
  72%      { opacity: 0.5; }
  78%      { opacity: 1; }
}
@keyframes t404-flash {
  0%   { opacity: 0; }
  15%  { opacity: 0.95; }
  100% { opacity: 0; }
}
.t404-destruct {
  animation: t404-shake 130ms infinite, t404-flicker 700ms infinite;
}
`;
